import { secondaryFontFamily, styled } from '@openeducation/oe-rn-themes';
import * as React from 'react';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';

import { useToggleButtonColors } from './use-toggle-button-colors';
import type { UseTypeModeColorsProps } from '../../hooks/use-type-mode-colors';
import { ml, width } from '../../style-utils';
import { OEText } from '../oe-text/oe-text';

export type CheckbokHorizontalAlignmentType = 'center' | 'space-between';

export interface ToggleButtonProps<T> extends UseTypeModeColorsProps {
  value?: T;
  label: string;
  selected?: boolean;
  style?: StyleProp<TextStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  horizontalAlignment?: CheckbokHorizontalAlignmentType;
  onChange?: (value?: T) => void;
  sparseRight?: boolean;
}

export const ToggleButton = <T extends unknown>({
  label,
  value,
  selected,
  horizontalAlignment = 'space-between',
  onChange,
  mode = 'contained',
  type = 'primary',
  ghost,
  color,
  disabled,
  style,
  containerStyle,
  sparseRight,
}: ToggleButtonProps<T>) => {
  const {
    borderColor,
    labelColor,
    backgroundColor,
    noSelectedBackgroundColor,
  } = useToggleButtonColors({
    selected,
    mode,
    type,
    ghost,
    color,
    disabled,
  });

  return (
    <Container
      disabled={disabled}
      style={containerStyle}
      onPress={() => onChange?.(value)}
      selected={selected}
      horizontalAlignment={horizontalAlignment}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      noSelectedBackgroundColor={noSelectedBackgroundColor}
    >
      <ItemLabel
        sparseRight={sparseRight}
        style={style}
        selected={selected}
        labelColor={labelColor}
        fontFamily={secondaryFontFamily}
        fontType={selected ? 'bold' : 'light'}
      >
        {label}
      </ItemLabel>
      {selected ? (
        <CheckIcon sparseRight={sparseRight} labelColor={labelColor} />
      ) : (
        <View style={[width(18), sparseRight && ml('auto')]} />
      )}
    </Container>
  );
};

type ContainerProp = {
  selected?: boolean;
  horizontalAlignment?: CheckbokHorizontalAlignmentType;
  backgroundColor?: string;
  noSelectedBackgroundColor?: string;
  borderColor?: string;
};

const Container = styled.TouchableOpacity<ContainerProp>`
  padding: 8px;
  border-radius: 4px;
  flex-direction: row;
  border-width: 1px;
  border-color: ${({ borderColor }) => borderColor};
  justify-content: ${({ horizontalAlignment }) => horizontalAlignment};
  background-color: ${({
    selected,
    backgroundColor,
    noSelectedBackgroundColor,
  }) => (selected ? backgroundColor : noSelectedBackgroundColor)};
`;

type ItemLabelProp = {
  selected?: boolean;
  labelColor?: string;
  sparseRight?: boolean;
};

export const ItemLabel = styled(OEText)<ItemLabelProp>`
  margin-right: 8px;
  color: ${({ labelColor }) => labelColor};
  ${({ sparseRight }) => sparseRight && 'margin-left: auto'};
`;

type CheckIconProps = {
  labelColor: string;
  sparseRight?: boolean;
};
const CheckIcon = styled(FontAwesome5).attrs(() => ({
  size: 16,
  name: 'check',
}))<CheckIconProps>`
  align-self: center;
  color: ${({ labelColor }) => labelColor};
  ${({ sparseRight }) => sparseRight && 'margin-left: auto'};
`;

export default ToggleButton;
